import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, FileDown, X, Diamond } from "lucide-react";
import jsPDF from "jspdf";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { registerPdfFont } from "../lib/pdfFonts";
import { resolveAssignees } from "../lib/assignees";
import { AssigneeAvatars } from "./AssigneeAvatars";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const SCALES = { gun: 28, hafta: 14, ay: 6.5, yil: 2.3 };
const MS_DAY = 86400000;
const GROUP_COLORS = ["#007AFF", "#AF52DE", "#FF9500", "#30B0C7", "#34C759", "#FF2D55", "#5856D6", "#FFCC00"];

function parseDate(s) {
  return s ? new Date(s + "T00:00:00") : null;
}

function shiftDate(iso, days) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const TimelineView = ({ board, onTaskClick, reload }) => {
  const { columns, tasks } = board;
  const milestones = board.milestones || [];
  const colMap = useMemo(() => Object.fromEntries(columns.map((c) => [c.id, c])), [columns]);

  const { groups, rangeStart, months, totalDays } = useMemo(() => {
    const dated = tasks.filter((t) => t.start_date || t.due_date);
    const today = new Date();
    let min = today, max = today;
    dated.forEach((t) => {
      const s = parseDate(t.start_date) || parseDate(t.due_date);
      const e = parseDate(t.due_date) || parseDate(t.start_date);
      if (s < min) min = s;
      if (e > max) max = e;
    });
    milestones.forEach((m) => {
      const d = parseDate(m.date);
      if (d < min) min = d;
      if (d > max) max = d;
    });
    const rangeStart = new Date(min.getFullYear(), min.getMonth(), 1);
    const rangeEnd = new Date(max.getFullYear(), max.getMonth() + 2, 0);
    const months = [];
    let cur = new Date(rangeStart);
    while (cur <= rangeEnd) {
      const daysInMonth = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
      months.push({ label: cur.toLocaleDateString("tr-TR", { month: "short", year: "2-digit" }), days: daysInMonth });
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    const totalDays = Math.round((rangeEnd - rangeStart) / MS_DAY) + 1;
    const groupNames = [...new Set(dated.map((t) => t.group || "Genel"))];
    const groups = groupNames.map((name, gi) => ({
      name,
      color: GROUP_COLORS[gi % GROUP_COLORS.length],
      tasks: dated.filter((t) => (t.group || "Genel") === name)
        .sort((a, b) => (a.start_date || a.due_date || "").localeCompare(b.start_date || b.due_date || "")),
    }));
    return { groups, rangeStart, months, totalDays };
  }, [tasks, milestones]);

  const dayOffset = (d) => Math.round((d - rangeStart) / MS_DAY);
  const todayOffset = dayOffset(new Date());

  const [zoom, setZoom] = useState("ay");
  const dayPx = SCALES[zoom];
  const chartWidth = totalDays * dayPx;
  const scrollRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [msOpen, setMsOpen] = useState(false);
  const [msName, setMsName] = useState("");
  const [msDate, setMsDate] = useState("");
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = Math.max(0, todayOffset * dayPx - 480);
  }, [zoom, dayPx, todayOffset]);

  const addMilestone = async (e) => {
    e.preventDefault();
    if (!msName.trim() || !msDate) return;
    try {
      await api.post(`/projects/${board.project.id}/milestones`, { name: msName.trim(), date: msDate });
      setMsOpen(false);
      setMsName("");
      setMsDate("");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeMilestone = async (m) => {
    if (!window.confirm(`"${m.name}" milestone'u silinsin mi?`)) return;
    await api.delete(`/milestones/${m.id}`);
    reload();
  };

  const exportTimeline = async () => {
    setExporting(true);
    try {
      const tr = (s) => s || "";
      const hex = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const FONT = await registerPdfFont(pdf);
      const PW = 297, PH = 210, MX = 10, chartX = 80;
      const chartW = PW - MX - chartX;
      const scale = chartW / totalDays;
      const dx = (d) => chartX + dayOffset(d) * scale;

      const headY = 28, headH = 7, footH = 12;
      const rows = [];
      groups.forEach((g) => { rows.push({ type: "group", g }); g.tasks.forEach((t) => rows.push({ type: "task", t })); });
      const topY = headY + headH;
      const avail = PH - topY - footH;
      const rowH = rows.length * 5.2 <= avail ? Math.min(8, avail / rows.length) : 5.6;
      const perPage = Math.max(1, Math.floor(avail / rowH));
      const pages = [];
      for (let i = 0; i < rows.length; i += perPage) pages.push(rows.slice(i, i + perPage));

      const drawHeader = (bottomY) => {
        let mx = chartX;
        pdf.setFont(FONT, "normal");
        pdf.setFontSize(7.5);
        months.forEach((m, i) => {
          const w = m.days * scale;
          if (i % 2) { pdf.setFillColor(245, 246, 248); pdf.rect(mx, headY, w, headH, "F"); }
          pdf.setTextColor(120, 126, 138);
          if (w > 10) pdf.text(tr(m.label.toUpperCase()), mx + w / 2, headY + 4.6, { align: "center" });
          mx += w;
        });
        mx = chartX;
        pdf.setDrawColor(228, 231, 236);
        pdf.setLineWidth(0.15);
        pdf.line(chartX, headY, chartX, bottomY);
        months.forEach((m) => { mx += m.days * scale; pdf.line(mx, headY, mx, bottomY); });
      };

      const drawGroupRow = (g, y) => {
        const [r, gg, b] = hex(g.color);
        pdf.setFillColor(r + (255 - r) * 0.92, gg + (255 - gg) * 0.92, b + (255 - b) * 0.92);
        pdf.rect(MX, y, PW - 2 * MX, rowH, "F");
        pdf.setFillColor(r, gg, b);
        pdf.rect(MX, y, 1.2, rowH, "F");
        pdf.setFont(FONT, "bold");
        pdf.setFontSize(8);
        pdf.setTextColor(r * 0.6, gg * 0.6, b * 0.6);
        let name = tr(g.name);
        while (pdf.getTextWidth(name) > chartX - MX - 22 && name.length > 4) name = name.slice(0, -2) + "…";
        pdf.text(name, MX + 3.2, y + rowH / 2 + 1.1);
        const doneN = g.tasks.filter((t) => colMap[t.column_id]?.is_done).length;
        pdf.setFont(FONT, "normal");
        pdf.setFontSize(6.5);
        pdf.setTextColor(140, 145, 155);
        pdf.text(`${doneN}/${g.tasks.length} tamam`, chartX - 3, y + rowH / 2 + 1, { align: "right" });
        const offs = g.tasks.map((t) => [dayOffset(parseDate(t.start_date || t.due_date)), dayOffset(parseDate(t.due_date || t.start_date))]);
        const gs = chartX + Math.min(...offs.map((o) => o[0])) * scale;
        const ge = chartX + (Math.max(...offs.map((o) => o[1])) + 1) * scale;
        const cy = y + rowH / 2;
        pdf.setFillColor(r, gg, b);
        pdf.roundedRect(gs, cy - 0.55, Math.max(ge - gs, 1.5), 1.1, 0.55, 0.55, "F");
        [gs, ge].forEach((x) => {
          pdf.triangle(x, cy - 1.6, x + 1.6, cy, x, cy + 1.6, "F");
          pdf.triangle(x, cy - 1.6, x - 1.6, cy, x, cy + 1.6, "F");
        });
      };

      const drawTaskRow = (t, y) => {
        const s = parseDate(t.start_date || t.due_date);
        const e = parseDate(t.due_date || t.start_date);
        const color = colMap[t.column_id]?.color || "#8E8E93";
        const [r, g2, b] = hex(color);
        const isDone = colMap[t.column_id]?.is_done;
        pdf.setFont(FONT, "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(55, 60, 70);
        let title = tr(t.title);
        while (pdf.getTextWidth(title) > chartX - MX - 9 && title.length > 4) title = title.slice(0, -2) + "…";
        pdf.text(title, MX + 5.5, y + rowH / 2 + 1);
        pdf.setFillColor(r, g2, b);
        pdf.circle(MX + 3, y + rowH / 2, 0.9, "F");
        const bx = dx(s);
        const bw = Math.max(dx(e) + scale - bx, 2.5);
        const bh = Math.min(rowH - 2.2, 4.8);
        const by = y + (rowH - bh) / 2;
        if (isDone) {
          pdf.setFillColor(r, g2, b);
          pdf.roundedRect(bx, by, bw, bh, 1.2, 1.2, "F");
        } else {
          pdf.setFillColor(r + (255 - r) * 0.85, g2 + (255 - g2) * 0.85, b + (255 - b) * 0.85);
          pdf.setDrawColor(r, g2, b);
          pdf.setLineWidth(0.35);
          pdf.roundedRect(bx, by, bw, bh, 1.2, 1.2, "FD");
        }
        pdf.setFontSize(6.3);
        let label = tr(t.title);
        if (pdf.getTextWidth(label) + 3.5 <= bw) {
          if (isDone) pdf.setTextColor(255, 255, 255);
          else pdf.setTextColor(r * 0.72, g2 * 0.72, b * 0.72);
          pdf.text(label, bx + 1.8, by + bh / 2 + 0.85);
        } else {
          while (pdf.getTextWidth(label) > 34 && label.length > 4) label = label.slice(0, -2) + "…";
          pdf.setTextColor(125, 130, 142);
          const lx2 = bx + bw + 1.5 + pdf.getTextWidth(label) > PW - MX ? bx - pdf.getTextWidth(label) - 1.5 : bx + bw + 1.5;
          pdf.text(label, lx2, by + bh / 2 + 0.85);
        }
      };

      const drawOverlays = (bottomY) => {
        const laneEnd = [-Infinity, -Infinity];
        [...milestones].sort((a, b) => a.date.localeCompare(b.date)).forEach((m) => {
          const x = dx(parseDate(m.date));
          pdf.setDrawColor(175, 82, 222);
          pdf.setLineWidth(0.3);
          pdf.setLineDashPattern([1, 1], 0);
          pdf.line(x, headY, x, bottomY);
          pdf.setLineDashPattern([], 0);
          pdf.setFillColor(175, 82, 222);
          pdf.triangle(x, headY - 4.2, x + 1.4, headY - 2.8, x - 1.4, headY - 2.8, "F");
          pdf.triangle(x, headY - 1.4, x + 1.4, headY - 2.8, x - 1.4, headY - 2.8, "F");
          pdf.setFontSize(6.5);
          pdf.setTextColor(175, 82, 222);
          let name = tr(m.name);
          while (pdf.getTextWidth(name) > 42 && name.length > 4) name = name.slice(0, -2) + "…";
          const lx0 = Math.min(x + 2.2, PW - MX - pdf.getTextWidth(name));
          const lane = lx0 >= laneEnd[0] ? 0 : lx0 >= laneEnd[1] ? 1 : -1;
          if (lane >= 0) {
            pdf.text(name, lx0, headY - 2 - lane * 4.4);
            laneEnd[lane] = lx0 + pdf.getTextWidth(name) + 2;
          }
        });
        if (todayOffset >= 0 && todayOffset <= totalDays) {
          const tx = chartX + todayOffset * scale;
          pdf.setDrawColor(255, 45, 85);
          pdf.setLineWidth(0.4);
          pdf.line(tx, headY, tx, bottomY);
          pdf.setFillColor(255, 45, 85);
          pdf.roundedRect(tx - 6.5, headY - 5.6, 13, 4.2, 2.1, 2.1, "F");
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(6.5);
          pdf.text("Bugün", tx, headY - 2.8, { align: "center" });
        }
      };

      const drawFooter = (bottomY, pageNo) => {
        const ly = bottomY + 5.4;
        pdf.setFontSize(6.5);
        pdf.setTextColor(140, 145, 155);
        pdf.text("Statü:", MX, ly + 0.9);
        let lx = MX + pdf.getTextWidth("Statü:") + 2.5;
        columns.forEach((c) => {
          const [r, g2, b] = hex(c.color);
          const w = pdf.getTextWidth(tr(c.name)) + 6;
          pdf.setFillColor(r + (255 - r) * 0.85, g2 + (255 - g2) * 0.85, b + (255 - b) * 0.85);
          pdf.roundedRect(lx, ly - 1.7, w, 4, 2, 2, "F");
          pdf.setFillColor(r, g2, b);
          pdf.circle(lx + 2.2, ly + 0.3, 0.9, "F");
          pdf.setTextColor(r * 0.6, g2 * 0.6, b * 0.6);
          pdf.text(tr(c.name), lx + 4.2, ly + 0.9);
          lx += w + 2.5;
        });
        pdf.setTextColor(170, 175, 185);
        pdf.text(pages.length > 1 ? `plena.pro · sayfa ${pageNo}/${pages.length}` : "plena.pro", PW - MX, ly + 0.9, { align: "right" });
      };

      const drawTitle = (pageNo) => {
        pdf.setFont(FONT, "bold");
        pdf.setFontSize(14);
        pdf.setTextColor(20, 20, 25);
        pdf.text(tr(`${board.project.name} — Timeline`), MX, 13);
        pdf.setFont(FONT, "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(140, 145, 155);
        const dateStr = tr(new Date().toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }));
        pdf.text(pageNo > 1 ? `${dateStr} · devam (${pageNo})` : dateStr, PW - MX, 13, { align: "right" });
      };

      pages.forEach((pageRows, pi) => {
        if (pi > 0) pdf.addPage();
        const bottomY = topY + pageRows.length * rowH;
        drawTitle(pi + 1);
        drawHeader(bottomY);
        let y = topY;
        pageRows.forEach((row) => {
          if (row.type === "group") drawGroupRow(row.g, y);
          else drawTaskRow(row.t, y);
          pdf.setDrawColor(236, 238, 242);
          pdf.setLineWidth(0.1);
          pdf.line(MX, y + rowH, PW - MX, y + rowH);
          y += rowH;
        });
        drawOverlays(bottomY);
        drawFooter(bottomY, pi + 1);
      });

      pdf.save(`${board.project.name.replace(/\s+/g, "-")}-timeline.pdf`);
    } catch (err) {
      toast.error("Görsel rapor oluşturulamadı: " + err.message);
    }
    setExporting(false);
  };

  const [preview, setPreview] = useState(null);

  const applyDelta = (sISO, dISO, mode, delta) => {
    let ns = sISO, nd = dISO;
    if (mode === "move") { ns = shiftDate(sISO, delta); nd = shiftDate(dISO, delta); }
    else if (mode === "start") { ns = shiftDate(sISO, delta); if (ns > nd) ns = nd; }
    else { nd = shiftDate(dISO, delta); if (nd < ns) nd = ns; }
    return [ns, nd];
  };

  const beginDrag = (e, t, mode) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const sISO = t.start_date || t.due_date;
    const dISO = t.due_date || t.start_date;
    let moved = false;
    const onMove = (ev) => {
      if (Math.abs(ev.clientX - startX) > 3) moved = true;
      const delta = Math.round((ev.clientX - startX) / dayPx);
      const [ns, nd] = applyDelta(sISO, dISO, mode, delta);
      setPreview({ taskId: t.id, start: ns, due: nd });
    };
    const onUp = async (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (!moved) { setPreview(null); onTaskClick(t); return; }
      const delta = Math.round((ev.clientX - startX) / dayPx);
      const [ns, nd] = applyDelta(sISO, dISO, mode, delta);
      if (ns === sISO && nd === dISO) { setPreview(null); return; }
      try {
        await api.patch(`/tasks/${t.id}`, { start_date: ns, due_date: nd });
        await reload();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      }
      setPreview(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  if (groups.length === 0) {
    return (
      <div data-testid="timeline-container" className="flex-1 flex flex-col items-center justify-center text-center">
        <p className="text-slate-500 text-sm">Timeline için tasklara başlangıç / bitiş tarihi ekleyin.</p>
        <p className="text-slate-400 text-xs mt-1.5">Task detayından tarih atadığınızda burada Gantt görünümünde izleyebilirsiniz.</p>
      </div>
    );
  }

  return (
    <div data-testid="timeline-container" className="flex-1 bg-white/90 dark:bg-[#1C1C1E]/90 backdrop-blur-md rounded-2xl border border-slate-200/70 dark:border-white/10 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] overflow-hidden flex flex-col">
      <div ref={scrollRef} className="flex-1 overflow-auto">
        <div className="flex min-w-full w-fit">
          {/* Left fixed column */}
          <div className="sticky left-0 z-20 w-[300px] shrink-0 bg-white/95 dark:bg-[#1C1C1E]/95 backdrop-blur-md border-r border-slate-200/80 dark:border-white/10">
            <div className="h-12 border-b border-slate-200/80 dark:border-white/10 flex items-center px-5">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Görev Grupları</span>
            </div>
            {groups.map((g) => (
              <div key={g.name}>
                <div data-testid="timeline-group-header" className="h-11 flex items-center gap-1.5 px-5 bg-slate-50/80 dark:bg-white/5 border-b border-slate-100 dark:border-white/5 border-l-[3px]" style={{ borderLeftColor: g.color }}>
                  <ChevronRight size={13} className="text-slate-400" />
                  <span className="text-sm font-bold text-slate-800 dark:text-slate-100 tracking-tight truncate" title={g.name}>{g.name}</span>
                  <span className="text-[10px] font-semibold text-slate-400 ml-auto bg-white dark:bg-white/10 rounded-full px-2 py-0.5 border border-slate-100 dark:border-white/10">
                    {g.tasks.filter((t) => colMap[t.column_id]?.is_done).length}/{g.tasks.length} tamam
                  </span>
                </div>
                {g.tasks.map((t) => (
                  <div key={t.id} onClick={() => onTaskClick(t)}
                    className="h-12 flex flex-col justify-center px-5 pl-9 border-b border-slate-50 dark:border-white/5 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                    <div className="flex items-center min-w-0">
                      <span className="w-2 h-2 rounded-full mr-2.5 shrink-0" style={{ background: colMap[t.column_id]?.color || "#8E8E93" }} />
                      <span className="text-[13px] font-medium text-slate-700 dark:text-slate-300 truncate">{t.title}</span>
                    </div>
                    <span className="text-[10px] text-slate-400 pl-[18px]">
                      {new Date((t.start_date || t.due_date) + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "short" })}
                      {" → "}
                      {new Date((t.due_date || t.start_date) + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "short" })}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Chart area */}
          <div className="relative shrink-0" style={{ width: chartWidth }}>
            <div className="h-12 border-b border-slate-200/80 dark:border-white/10 flex sticky top-0 bg-white/95 dark:bg-[#1C1C1E]/95 backdrop-blur-md z-10">
              {months.map((m, i) => (
                <div key={i} className={`border-r border-slate-100 dark:border-white/5 flex items-center justify-center text-xs font-semibold text-slate-500 uppercase tracking-wide ${i % 2 ? "bg-slate-50/70 dark:bg-white/5" : ""}`}
                  style={{ width: m.days * dayPx }}>
                  {m.label}
                </div>
              ))}
            </div>

            {/* Month grid lines */}
            <div className="absolute top-12 bottom-0 left-0 right-0 flex pointer-events-none">
              {months.map((m, i) => (
                <div key={i} className={`border-r border-slate-100/80 dark:border-white/5 h-full ${i % 2 ? "bg-slate-50/40 dark:bg-white/[0.03]" : ""}`}
                  style={{
                    width: m.days * dayPx,
                    ...(dayPx >= 14 ? { backgroundImage: `repeating-linear-gradient(to right, rgba(15,23,42,0.04) 0px, rgba(15,23,42,0.04) 1px, transparent 1px, transparent ${dayPx}px)` } : {}),
                  }} />
              ))}
            </div>

            {/* Today marker */}
            {todayOffset >= 0 && todayOffset <= totalDays && (
              <div data-testid="timeline-today-marker" className="absolute top-0 bottom-0 z-10 pointer-events-none" style={{ left: todayOffset * dayPx }}>
                <div className="absolute top-1 -translate-x-1/2 bg-[#FF2D55] text-white text-[10px] font-bold rounded-full px-2 py-0.5 whitespace-nowrap shadow-lg shadow-[#FF2D55]/30">
                  Bugün
                </div>
                <span className="absolute top-8 -translate-x-1/2 w-2 h-2 rounded-full bg-[#FF2D55] animate-ping" />
                <div className="w-px h-full bg-[#FF2D55]/70 shadow-[0_0_8px_rgba(255,45,85,0.4)]" />
              </div>
            )}

            {/* Milestones */}
            {milestones.map((m, mi) => {
              const mo = dayOffset(parseDate(m.date));
              if (mo < 0 || mo > totalDays) return null;
              const prev = mi > 0 ? dayOffset(parseDate(milestones[mi - 1].date)) : null;
              const crowded = prev !== null && (mo - prev) * dayPx < 120;
              return (
                <div key={m.id} data-testid="timeline-milestone-marker" className="absolute top-12 bottom-0 z-[9] hover:z-20" style={{ left: mo * dayPx }}>
                  <div className="absolute top-0 bottom-0 border-l border-dashed border-[#AF52DE]/60" />
                  <div className={`group/ms absolute flex items-center gap-1.5 -translate-x-[5px] ${crowded && mi % 2 ? "top-[26px]" : "top-1.5"}`}>
                    <span className="w-2.5 h-2.5 rotate-45 bg-[#AF52DE] rounded-[2px] shadow shrink-0" title={m.name} />
                    <span className="text-[10px] font-bold text-[#AF52DE] whitespace-nowrap bg-white/85 dark:bg-[#1C1C1E]/85 rounded px-1 py-0.5 max-w-[160px] truncate group-hover/ms:max-w-none" title={m.name}>
                      {m.name} · {new Date(m.date + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "short" })}
                    </span>
                    <button data-testid="timeline-milestone-delete-button" onClick={() => removeMilestone(m)}
                      className="opacity-0 group-hover/ms:opacity-100 text-[#AF52DE]/70 hover:text-[#FF2D55] transition-opacity">
                      <X size={11} />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Rows */}
            {groups.map((g) => (
              <div key={g.name}>
                <div className="h-11 border-b border-slate-100 dark:border-white/5 relative" style={{ background: `${g.color}0D` }}>
                  {(() => {
                    const offs = g.tasks.map((t) => [
                      dayOffset(parseDate(t.start_date || t.due_date)),
                      dayOffset(parseDate(t.due_date || t.start_date)),
                    ]);
                    const gs = Math.min(...offs.map((o) => o[0]));
                    const ge = Math.max(...offs.map((o) => o[1]));
                    return (
                      <span data-testid="timeline-group-span" className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full"
                        style={{ left: gs * dayPx, width: Math.max((ge - gs + 1) * dayPx, 8), background: `${g.color}99` }}>
                        <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rotate-45 rounded-[2px]" style={{ background: g.color }} />
                        <span className="absolute -right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rotate-45 rounded-[2px]" style={{ background: g.color }} />
                      </span>
                    );
                  })()}
                </div>
                {g.tasks.map((t) => {
                  const pv = preview && preview.taskId === t.id ? preview : null;
                  const s = parseDate(pv ? pv.start : (t.start_date || t.due_date));
                  const e = parseDate(pv ? pv.due : (t.due_date || t.start_date));
                  const left = dayOffset(s) * dayPx;
                  const width = Math.max((dayOffset(e) - dayOffset(s) + 1) * dayPx, 28);
                  const color = colMap[t.column_id]?.color || "#8E8E93";
                  const isDone = colMap[t.column_id]?.is_done;
                  const subT = (t.subtasks || []).length;
                  const subD = (t.subtasks || []).filter((x) => x.done).length;
                  const progress = isDone ? 100 : subT > 0 ? (subD / subT) * 100 : 0;
                  return (
                    <div key={t.id} className="h-12 border-b border-slate-50 dark:border-white/5 relative">
                      <div
                        data-testid="timeline-task-bar"
                        onPointerDown={(ev) => beginDrag(ev, t, "move")}
                        title={`${t.title} (${t.start_date || "?"} → ${t.due_date || "?"}) — sürükleyerek taşı, kenarlardan uzat`}
                        className={`group/bar absolute top-2 h-8 rounded-lg flex items-center overflow-hidden select-none shadow-sm ${pv ? "cursor-grabbing ring-2 ring-[#007AFF]/30 z-20" : "cursor-grab hover:shadow-md"}`}
                        style={{
                          left, width, touchAction: "none",
                          background: isDone ? `${color}` : `${color}14`,
                          border: `1.5px solid ${color}`,
                        }}
                      >
                        {!isDone && progress > 0 && (
                          <span className="absolute inset-y-0 left-0 pointer-events-none rounded-r"
                            style={{ width: `${progress}%`, background: `${color}30` }} />
                        )}
                        <span className="relative text-[11px] font-semibold truncate pointer-events-none px-2.5" style={{ color: isDone ? "#fff" : color }}>
                          {width > 70 ? t.title : ""}
                        </span>
                        {width > 56 && (
                          <AssigneeAvatars assignees={resolveAssignees(t, board)} size="sm" max={width > 130 ? 2 : 1} className="relative ml-auto mr-1.5 pointer-events-none shrink-0" />
                        )}
                        <span onPointerDown={(ev) => beginDrag(ev, t, "start")} data-testid="timeline-bar-resize-start"
                          className="absolute left-0 top-0 h-full w-2 cursor-ew-resize rounded-l-md opacity-0 group-hover/bar:opacity-60"
                          style={{ background: color, touchAction: "none" }} />
                        <span onPointerDown={(ev) => beginDrag(ev, t, "end")} data-testid="timeline-bar-resize-end"
                          className="absolute right-0 top-0 h-full w-2 cursor-ew-resize rounded-r-md opacity-0 group-hover/bar:opacity-60"
                          style={{ background: color, touchAction: "none" }} />
                      </div>
                      {pv && (
                        <span className="absolute -top-1.5 z-30 text-[9px] font-bold text-white bg-[#007AFF] rounded-full px-1.5 py-0.5 whitespace-nowrap pointer-events-none shadow-lg"
                          style={{ left: left }}>
                          {new Date(pv.start + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "short" })} → {new Date(pv.due + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "short" })}
                        </span>
                      )}
                      {width <= 70 && (
                        <span className="absolute top-2 h-8 flex items-center text-[11px] font-semibold text-slate-500 pointer-events-none whitespace-nowrap"
                          style={{ left: left + width + 6 }}>
                          {t.title}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Filler for wide screens */}
          <div className="flex-1 min-w-0">
            <div className="h-12 border-b border-slate-200/80 dark:border-white/10 bg-white/95 dark:bg-[#1C1C1E]/95" />
          </div>
        </div>
      </div>

      <div className="border-t border-slate-200/70 dark:border-white/10 px-5 py-2.5 flex items-center gap-5 bg-white/80 dark:bg-[#1C1C1E]/80">
        {columns.map((c) => (
          <span key={c.id} className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} /> {c.name}
          </span>
        ))}
        <span className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF2D55]" /> Bugün çizgisi
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="timeline-add-milestone-button" onClick={() => setMsOpen(true)}
            className="h-7 px-3 rounded-lg bg-[#AF52DE]/10 hover:bg-[#AF52DE]/20 text-[#AF52DE] text-[11px] font-semibold transition-all flex items-center gap-1.5">
            <Diamond size={11} /> Milestone
          </button>
          <button data-testid="timeline-export-pdf-button" onClick={exportTimeline} disabled={exporting}
            className="h-7 px-3 rounded-lg bg-slate-900 hover:bg-slate-700 dark:bg-white/15 dark:hover:bg-white/25 text-white text-[11px] font-semibold transition-all flex items-center gap-1.5 disabled:opacity-60">
            {exporting ? <span className="w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin" /> : <FileDown size={12} />}
            Görsel Rapor
          </button>
          <div className="bg-slate-100 dark:bg-white/10 p-0.5 rounded-lg flex items-center gap-0.5 border border-slate-200/70 dark:border-white/10">
            {[["gun", "Gün"], ["hafta", "Hafta"], ["ay", "Ay"], ["yil", "Yıl"]].map(([k, label]) => (
              <button key={k} data-testid={`timeline-zoom-${k}`} onClick={() => setZoom(k)}
                className={`h-6 px-2.5 rounded-md text-[11px] font-semibold transition-all ${zoom === k ? "bg-white dark:bg-[#3A3A3C] shadow-sm text-slate-900 dark:text-white" : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={msOpen} onOpenChange={setMsOpen}>
        <DialogContent className="rounded-2xl sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Yeni Milestone</DialogTitle></DialogHeader>
          <form onSubmit={addMilestone} className="space-y-4 mt-2">
            <input data-testid="milestone-name-input" required autoFocus value={msName}
              onChange={(e) => setMsName(e.target.value)} placeholder="örn: Lansman"
              className="w-full h-10 px-3.5 rounded-xl bg-slate-100/80 dark:bg-white/10 border border-slate-200/60 dark:border-white/10 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-[#AF52DE]/50 transition-all" />
            <input data-testid="milestone-date-input" required type="date" value={msDate}
              onChange={(e) => setMsDate(e.target.value)}
              className="w-full h-10 px-3.5 rounded-xl bg-slate-100/80 dark:bg-white/10 border border-slate-200/60 dark:border-white/10 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-[#AF52DE]/50 transition-all" />
            <button data-testid="milestone-create-submit-button" type="submit"
              className="w-full h-10 rounded-xl bg-[#AF52DE] hover:bg-[#9A3FC9] text-white text-sm font-semibold transition-all">
              Milestone Ekle
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimelineView;
